import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import { Box, Chip, Link, Typography } from '@mui/material'
import React, { ChangeEvent, DragEvent, useState } from 'react'

// STYLES
const CSV_LINK_STYLE = {
  ':hover': { cursor: 'pointer', textDecoration: 'underline' },
}
const UPLOAD_BOX_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 2,
  flex: '1 0 0',
  gap: 1,
  border: '2px dashed #E5E5E5',
  background: '#F8F8F8',
}

interface FileUploadBoxProps {
  id?: string
  onFileSelect: (file: File | null) => void
  acceptedFormats: string[]
  maxSizeInMB?: number
}

const generateUniqueId = (id?: string) => {
  return id ? id : Math.random().toString(36).substring(2, 15)
}

const FileUploadBox: React.FC<FileUploadBoxProps> = ({
  id,
  onFileSelect,
  acceptedFormats = [],
  maxSizeInMB = 15,
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileUploadId = generateUniqueId(id)

  const validateFile = (file: File): boolean => {
    if (!acceptedFormats.includes(`.${file.name.split('.').pop()}`)) {
      setError(
        `Invalid file format. Accepted formats are: ${acceptedFormats.join(', ')}`,
      )
      return false
    }
    if (file.size > maxSizeInMB * 1024 * 1024) {
      setError(`File size exceeds ${maxSizeInMB}MB limit`)
      return false
    }
    setError(null)
    return true
  }

  const handleFile = (file: File) => {
    if (validateFile(file)) {
      setFile(file)
      onFileSelect(file)
    } else {
      setFile(null)
      onFileSelect(null)
    }
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    const droppedFiles = event.dataTransfer.files
    if (droppedFiles.length > 0) {
      handleFile(droppedFiles[0])
    }
  }

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFile(event.target.files[0])
      event.target.value = ''
    }
  }

  const handleDelete = () => {
    setFile(null)
    onFileSelect(null)
    setError(null)
  }

  return (
    <Box
      sx={{
        ...UPLOAD_BOX_STYLE,
        backgroundColor: isDragging ? '#E5E5E5' : '#F8F8F8',
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {file ? (
        <Chip
          label={file.name}
          variant='outlined'
          color='secondary'
          onDelete={handleDelete}
        />
      ) : (
        <FileUploadOutlinedIcon />
      )}
      <Typography variant='body1'>
        <label htmlFor={`file-upload-${fileUploadId}`}>
          <Link underline='none' sx={CSV_LINK_STYLE}>
            Click to upload
          </Link>{' '}
        </label>
        or Drag & drop your data file
      </Typography>
      <Typography variant='body2'>
        Only{' '}
        {acceptedFormats.map((format, index) => (
          <React.Fragment key={format}>
            <strong>{format}</strong>
            {index < acceptedFormats.length - 1 ? ', ' : ' '}
          </React.Fragment>
        ))}{' '}
        format(s) are supported and size should not exceed {maxSizeInMB}MB
      </Typography>
      {error && (
        <Typography variant='body2' color='error'>
          {error}
        </Typography>
      )}
      <input
        type='file'
        accept={acceptedFormats.join(', ')}
        style={{ display: 'none' }}
        id={`file-upload-${fileUploadId}`}
        onChange={handleFileSelect}
      />
    </Box>
  )
}

export default FileUploadBox
